import type { Row } from '@tanstack/react-table';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { CreatorDiscoveryEmailResource, CreatorDiscoveryInstagramAccount } from '@frontend/app/hooks';
import { DiscoverInstagramCreatorsQuery, InstagramCreator } from '@frontend/gql/social/graphql';
import { Card } from '@frontend/shadcn/components/ui/card';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { ChevronLeftIcon, ChevronRightIcon } from '@revfluence/fresh-icons/solid';
import { Columns3Icon } from '@revfluence/fresh-icons/solid/esm';
import type { CategorizedLoadingStates } from '../hooks/use-loading-states';
import {
  TableHeader,
  TableHeaderIconButton,
  TableHeaderLayout,
  TableHeaderSection,
} from '../../Analytics/components/table-header';
import ColumnDialog from '../../Analytics/components/columnSelectorDialog';
import { useColumnDialog } from '../../Analytics/components/columnSelectorDialog/hooks/useColumnDialog';
import { ColumnDefinitionInput } from '../components/table/types';
import { QueryVariables } from '../types';
import { InstagramCreatorSearchTable } from './components/table';
import { ActionMenu } from './components/table/components/action-menu';
import { useInstagramCreatorSearchTable } from './components/table/hooks/useInstagramCreatorSearchTable';
import { useInstagramCreatorOverview } from './hooks/use-instagram-creator-overview';
import { InstagramCreatorOverview } from './instagram-creator-overview';
import { InstagramCreatorSearchEmptyState, BulkOperationAlert } from './components';
import { defaultInstagramColumns } from './components/table/config/default-instagram-columns';
import { useInstagramCreatorColumns } from './hooks/use-instagram-creator-columns';

interface InstagramCreatorSearchTabProps {
  data: DiscoverInstagramCreatorsQuery | undefined;
  clientId: string | undefined | null;
  limit: number;
  onPageChange: (cursor: string | null, direction: 'next' | 'prev') => void;
  emailResource?: CreatorDiscoveryEmailResource;
  instagramAccount?: CreatorDiscoveryInstagramAccount;
  handleClearAllFilters: () => void;
  hasSearchFilters: boolean;
  loadingStates: CategorizedLoadingStates;
  updateUrlParams?: (params: Partial<QueryVariables>) => void;
  handleSearch?: () => void;
  queryVariables?: QueryVariables;
  igdmActive?: boolean;
}

export const InstagramCreatorSearchTab = ({
  data,
  clientId,
  limit,
  onPageChange,
  emailResource,
  instagramAccount,
  handleClearAllFilters,
  hasSearchFilters,
  loadingStates,
  updateUrlParams,
  handleSearch,
  queryVariables,
  igdmActive,
}: InstagramCreatorSearchTabProps) => {
  const location = useLocation();
  const history = useHistory();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [originalCreator, setOriginalCreator] = useState<Pick<InstagramCreator, 'username'> | null>(null);

  const [campaignSearchTerm, setCampaignSearchTerm] = useState('');
  const [groupSearchTerm, setGroupSearchTerm] = useState('');

  const { selectedColumns, setSelectedColumns } = useInstagramCreatorColumns();
  const [editModeColumns, setEditModeColumns] = useState<ColumnDefinitionInput[]>(selectedColumns);

  useEffect(() => {
    setEditModeColumns(selectedColumns);
  }, [selectedColumns]);

  // Column dialog save handler with localStorage persistence
  const onSaveColumnInfo = useCallback(
    async (columns: ColumnDefinitionInput[]) => {
      setSelectedColumns(columns);
    },
    [setSelectedColumns],
  );

  const { columnDialogProps } = useColumnDialog({
    defaultSelectedColumns: defaultInstagramColumns,
    selectedColumns: editModeColumns,
    setSelectedColumns: setEditModeColumns,
    onSave: onSaveColumnInfo,
  });

  const instagramCreatorsData = useMemo(() => data?.discoverInstagramCreators?.creators ?? [], [data]);

  const paging = data?.discoverInstagramCreators?.paging ?? {
    cursors: { after: null, before: null },
    hasNext: false,
    hasPrevious: false,
  };
  const { cursors = { after: null, before: null }, hasNext = false, hasPrevious = false } = paging;
  const { after = null, before = null } = cursors;

  const creatorUsername = searchParams.get('creatorUsername');

  // Reset state when the user switches accounts
  useEffect(() => {
    if (instagramAccount?.igUserId) {
      setOriginalCreator(null);
    }
  }, [instagramAccount]);

  const handleSheetClose = () => {
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.delete('creatorUsername');
    history.replace({ search: newSearchParams.toString() });
  };

  const processCreatorClick = useCallback(
    (creator: Pick<InstagramCreator, 'username'>) => {
      // Update the URL
      const newSearchParams = new URLSearchParams(location.search);
      if (creator.username) {
        newSearchParams.set('creatorUsername', creator.username);
      }
      history.push({ search: newSearchParams.toString() });
    },
    [location.search, history],
  );

  const { instagramCreatorOverviewProps } = useInstagramCreatorOverview({
    instagramAccount,
    creatorUsername,
    clientId,
    onSheetShouldClose: handleSheetClose,
    processCreatorClick,
    originalCreator,
    setOriginalCreator,
    updateUrlParams,
    handleSearch,
    queryVariables,
    availableCreators: instagramCreatorsData,
    emailResource,
    igdmActive,
  });

  const handleRowClick = useCallback(
    (creator: InstagramCreator) => {
      processCreatorClick({ username: creator.username });
    },
    [processCreatorClick],
  );

  const { instagramTableProps, actionMenuProps, bulkOperationState, hideBulkOperationAlert } =
    useInstagramCreatorSearchTable({
      selectedColumns,
      setSelectedColumns,
      data: instagramCreatorsData,
      currentPage: limit,
      pageSize: 10,
      handleSortingChange: () => {
        console.log('handleSortingChange');
      },
      sorting: [],
      globalFilter: '',
      setIsPartialLoading: () => {
        console.log('setIsPartialLoading');
      },
      tableBodyTableRowProps: useCallback(
        (row: Row<InstagramCreator>) => ({
          onClick: instagramAccount?.igUserId ? () => handleRowClick(row.original) : undefined,
        }),
        [handleRowClick, instagramAccount?.igUserId],
      ),
      campaignSearchTerm,
      setCampaignSearchTerm,
      groupSearchTerm,
      setGroupSearchTerm,
      emailResource,
      igdmActive,
    });

  const handlePrevPage = useCallback(() => {
    if (before) {
      onPageChange(before, 'prev');
    }
  }, [before, onPageChange]);

  const handleNextPage = useCallback(() => {
    if (after) {
      onPageChange(after, 'next');
    }
  }, [after, onPageChange]);

  // Extract loading states from the centralized hook
  const { shouldShowMainSpinner, shouldShowPaginationLoader, shouldShowEmptyState, shouldDisableInteractions } =
    loadingStates.uiStates;

  return (
    <>
      {shouldShowMainSpinner ? (
        <Card className="mx-4 mb-4" aria-label="Loading Instagram Creators Search table">
          {/* Table header skeleton */}
          <div className="p-4 border-b">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Skeleton className="h-9 w-32 rounded-md" aria-label="Loading action menu" />
                <Skeleton className="h-9 w-24 rounded-md" aria-label="Loading filter options" />
              </div>
              <div className="flex items-center gap-1">
                <Skeleton className="h-8 w-8 rounded-md" aria-label="Loading previous button" />
                <Skeleton className="h-8 w-8 rounded-md" aria-label="Loading next button" />
              </div>
            </div>
          </div>
          {/* Table content skeleton */}
          <div className="p-4 space-y-3">
            {Array.from({ length: 8 }).map((_, index) => (
              <Skeleton key={index} className="h-12 w-full rounded-md" aria-label={`Loading table row ${index + 1}`} />
            ))}
          </div>
        </Card>
      ) : (
        <>
          {/* Bulk Operation Alert - Added above the Card as specified */}
          <BulkOperationAlert operationState={bulkOperationState} onDismiss={hideBulkOperationAlert} />

          <Card className="mx-4 mb-4" aria-label="Instagram Creators Search table">
            <TableHeader>
              <TableHeaderLayout>
                <TableHeaderSection>
                  <ActionMenu {...actionMenuProps} />
                </TableHeaderSection>
                <TableHeaderSection>
                  <ColumnDialog {...columnDialogProps}>
                    <TableHeaderIconButton
                      data-dd-action-name="open-column-selector"
                      aria-label="Select columns to display"
                    >
                      <Columns3Icon className="h-4 w-4" aria-hidden="true" />
                    </TableHeaderIconButton>
                  </ColumnDialog>
                  <nav aria-label="Table navigation" className="flex items-center">
                    <TableHeaderIconButton
                      onClick={handlePrevPage}
                      disabled={!hasPrevious || shouldDisableInteractions}
                      data-dd-action-name="members-table-previous-page"
                      aria-label="Previous page"
                      aria-disabled={!hasPrevious || shouldDisableInteractions}
                    >
                      <ChevronLeftIcon aria-hidden="true" />
                    </TableHeaderIconButton>
                    <TableHeaderIconButton
                      onClick={handleNextPage}
                      disabled={!hasNext || shouldDisableInteractions}
                      data-dd-action-name="members-table-next-page"
                      aria-label="Next page"
                      aria-disabled={!hasNext || shouldDisableInteractions}
                    >
                      <ChevronRightIcon aria-hidden="true" />
                    </TableHeaderIconButton>
                  </nav>
                </TableHeaderSection>
              </TableHeaderLayout>
            </TableHeader>
            {shouldShowPaginationLoader ? (
              <div className="mx-4 mb-4">
                <Skeleton className="h-[400px] rounded-xl" aria-label="Loading results table" />
              </div>
            ) : shouldShowEmptyState ? (
              <div className="p-8">
                <InstagramCreatorSearchEmptyState
                  onClearFilters={handleClearAllFilters}
                  hasSearchFilters={hasSearchFilters}
                />
              </div>
            ) : (
              <InstagramCreatorSearchTable
                {...instagramTableProps}
                updatingColumns={[]}
                aria-busy={shouldDisableInteractions}
                aria-label="Members data table"
              />
            )}
          </Card>
        </>
      )}
      <InstagramCreatorOverview {...instagramCreatorOverviewProps} />
    </>
  );
};
